import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IParteProcesso, StepPartesProps } from './types';
import PartesEnvolvidas from './PartesEnvolvidas';
import PartesRepresentadas from './PartesRepresentadas';
import {
  StepContainer,
  StepContent,
  SectionContainer,
  SectionTitle
} from './styles';
import { FormSectionTitle, StepHeader, HeaderContent, StepTitle, StepSubtitle } from '../styles';

const StepPartes: React.FC<StepPartesProps> = ({ 
  step, 
  currentStep, 
  setPartes, 
  setCurrentStep ,
  validando,
  setValidando,
  initialPartesProcesso,
  initialPartesRepresentadas
}) => {
  const [partesProcesso, setPartesProcesso] = useState<IParteProcesso[]>(initialPartesProcesso || []);
  const [partesRepresentadasManuais, setPartesRepresentadasManuais] = useState<string[]>(() => {
    if (initialPartesRepresentadas) {
      const autoRepresentadas = (initialPartesProcesso || []).filter(p => p.representada).map(p => p.nome);
      return initialPartesRepresentadas.filter(nome => !autoRepresentadas.includes(nome));
    }
    return [];
  });
  const [erros, setErros] = useState<any>({});

  // Deriva representadas criadas a partir das partes marcadas como representadas
  const partesRepresentadasAuto = useMemo(
    () => partesProcesso.filter(p => p.representada).map(p => p.nome),
    [partesProcesso]
  );

  // União de representadas (manuais + automáticas), sem duplicatas
  const todasRepresentadas = useMemo(() => {
    const conjunto = new Set<string>([...partesRepresentadasManuais, ...partesRepresentadasAuto]);
    return Array.from(conjunto);
  }, [partesRepresentadasManuais, partesRepresentadasAuto]);

  useEffect(() => {
    if (initialPartesProcesso) {
      setPartesProcesso(initialPartesProcesso);
    }
    if (initialPartesRepresentadas) {
      const autoRepresentadas = (initialPartesProcesso || []).filter(p => p.representada).map(p => p.nome);
      setPartesRepresentadasManuais(initialPartesRepresentadas.filter(nome => !autoRepresentadas.includes(nome)));
    }
  }, [initialPartesProcesso, initialPartesRepresentadas]);

  useEffect(() => {
    setPartes(partesProcesso, todasRepresentadas);
  }, [partesProcesso, todasRepresentadas, setPartes]);

  useEffect(() => {
    if(validando && step === currentStep) {
      const r = validarCampos();
      setValidando(false)
      if(r) {
        setCurrentStep(currentStep + 1);
      }
    }
  }, [validando]);

  // Validação simples (limites de tamanho garantidos por maxLength; step é opcional)
  const validarCampos = useCallback(() => {
    const novosErros: any = {};
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  }, []);

  const handleChangePartes = useCallback((novas: IParteProcesso[]) => {
    setPartesProcesso(novas);
    validarCampos();
  }, [validarCampos]);

  const handleAddRepresentada = useCallback((nome: string) => {
    const valor = nome.trim();
    if (!valor) return;
    setPartesRepresentadasManuais(prev => prev.includes(valor) ? prev : [...prev, valor]);
  }, []);

  const handleRemoveRepresentada = useCallback((nome: string) => {
    const valor = nome.trim();
    // Se está entre as automáticas, desmarca na origem (parte do processo)
    if (partesRepresentadasAuto.includes(valor)) {
      setPartesProcesso(prev => {
        const indice = prev.findIndex(p => p.nome === valor && p.representada);
        if (indice >= 0) {
          const novas = [...prev];
          novas[indice] = { ...novas[indice], representada: false };
          return novas;
        }
        return prev;
      });
    } else {
      // Caso contrário, remove da lista manual
      setPartesRepresentadasManuais(prev => prev.filter(n => n !== valor));
    }
  }, [partesRepresentadasAuto]);

  const isActive = currentStep === step;

  if (!isActive) {
    return null;
  }
  return (
    <StepContainer>
        <StepHeader>
        <HeaderContent>
          <StepTitle>Partes Envolvidas</StepTitle>
          <StepSubtitle>Defina as partes envolvidas no processo e quais serão representadas.</StepSubtitle>
        </HeaderContent>
      </StepHeader>

      <StepContent>
        <SectionContainer>
          <FormSectionTitle>Partes do Processo</FormSectionTitle>
          <PartesEnvolvidas
            partes={partesProcesso}
            onChange={handleChangePartes}
            erros={erros}
          />
        </SectionContainer>

        <SectionContainer>
          <FormSectionTitle>Partes que serão representadas:</FormSectionTitle>
          <PartesRepresentadas
            representadas={todasRepresentadas}
            autoRepresentadas={partesRepresentadasAuto}
            onAdd={handleAddRepresentada}
            onRemove={handleRemoveRepresentada}
            erros={erros}
          />
        </SectionContainer>
      </StepContent>
    </StepContainer>
  );
};

export default StepPartes;


